import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, RefreshCw } from "lucide-react";
import { listInterest, getEmailStatus, sendLondonLaunch } from "@/lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const SenderStatus = ({ status, onRecheck, checking }) => (
  <div data-testid="email-sender-status" className="flex flex-wrap items-center gap-3 text-sm">
    <span className="overline text-zinc-500">Sender</span>
    <span className="font-mono text-xs">{status?.sender || "…"}</span>
    <span
      data-testid="email-domain-status"
      className={`overline px-2 py-1 ${status?.verified ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}
    >
      {status ? `${status.domain}: ${status.status}` : "checking"}
    </span>
    {!status?.verified && (
      <button
        data-testid="email-recheck-btn"
        onClick={onRecheck}
        className="overline inline-flex items-center gap-1 text-[#002FA7] hover:text-[#FF2A00]"
      >
        <RefreshCw size={12} className={checking ? "animate-spin" : ""} /> Re-check verification
      </button>
    )}
  </div>
);

export const LondonListPanel = ({ token, onCount }) => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async (recheck = false) => {
    setChecking(true);
    try {
      const [list, st] = await Promise.all([listInterest(token), getEmailStatus(token, recheck)]);
      setItems(list);
      setStatus(st);
      onCount?.(list.length);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pending = items.filter((i) => !i.launch_sent_at).length;

  const onSend = async () => {
    setSending(true);
    try {
      const r = await sendLondonLaunch(token);
      toast.success(`Launch email is going out to ${r.queued} people.`);
      setTimeout(() => load(), 4000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the launch email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="london-list-panel">
      <div className="p-4 border-b border-zinc-200 flex flex-wrap items-center justify-between gap-4">
        <SenderStatus status={status} onRecheck={() => load(true)} checking={checking} />
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              data-testid="send-launch-btn"
              disabled={sending || pending === 0 || !status?.verified}
              className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 overline text-[11px] hover:bg-[#FF2A00] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Send launch email ({pending})
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="send-launch-confirm" className="rounded-none">
            <AlertDialogHeader>
              <AlertDialogTitle>Send the London launch email?</AlertDialogTitle>
              <AlertDialogDescription>
                "Registrations are now open — BoT Global, London March 2027" will be emailed to {pending} people
                who haven't received it yet. This can't be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="send-launch-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="send-launch-confirm-btn" onClick={onSend}>
                Send now
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      {!status?.verified && status && (
        <p data-testid="email-not-verified-note" className="px-4 py-3 text-xs text-amber-800 bg-amber-50 border-b border-amber-100">
          Visitor emails switch to {status.domain} automatically once it shows "verified". Until then the launch email is locked.
        </p>
      )}
      <table className="w-full text-sm" data-testid="interest-table">
        <thead className="bg-zinc-50 border-b border-zinc-200">
          <tr className="overline text-zinc-500">
            {["#", "Name", "Email", "Signed up", "Launch email"].map((h) => (
              <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr><td colSpan={5} className="p-8 text-center text-zinc-500">No sign-ups yet.</td></tr>
          ) : (
            items.map((it, i) => (
              <tr key={it.id} data-testid={`interest-row-${i}`} className="border-b border-zinc-100">
                <td className="px-4 py-3 text-zinc-500">{i + 1}</td>
                <td className="px-4 py-3 font-medium">{it.full_name}</td>
                <td className="px-4 py-3">{it.email}</td>
                <td className="px-4 py-3 text-zinc-500 font-mono text-xs">{new Date(it.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-xs">
                  {it.launch_sent_at ? <span className="text-green-700">Sent</span> : <span className="text-zinc-400">Not sent</span>}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
